import { format, isToday, isYesterday } from "date-fns";
import { marked } from "marked";
import { styleResolverLedgerHtml } from "../shared/utils/resolverLedger";
import {
  CHAT_CALLOUT_CONTAINER_STYLE,
  CHOOSE_NEXT_STEP_CARD_STYLE,
  INVESTIGATION_SUMMARY_BODY_STYLE,
  INVESTIGATION_SUMMARY_CARD_STYLE,
  INVESTIGATION_SUMMARY_TITLE_STYLE,
} from "../shared/utils/chatCalloutStyles";

export const formatTimestamp = (seconds?: string) => {
  if (!seconds) return "";

  const date = new Date(parseInt(seconds) * 1000);

  if (isToday(date)) {
    // Today: show time only (e.g., "2:30 PM")
    return format(date, "p");
  } else if (isYesterday(date)) {
    // Yesterday: show "Yesterday" and time (e.g., "Yesterday 2:30 PM")
    return `Yesterday ${format(date, "p")}`;
  } else {
    // Past days: show full date and time (e.g., "Dec 15, 2023 2:30 PM")
    return format(date, "MMM d, yyyy p");
  }
};

/** Lead icon + word styling for structured-workflow plan phase lines (read-only pre-check). */
function planVerificationLeadIconHtml(statusKey: string): string {
  const palette: Record<string, { c: string; g: string; t: string }> = {
    ready: { c: "#16a34a", g: "✓", t: "Ready" },
    partial: { c: "#ca8a04", g: "◐", t: "Partial" },
    missing: { c: "#dc2626", g: "✗", t: "Missing" },
    "no-op": { c: "#ca8a04", g: "⊘", t: "No-op" },
    "needs clarification": { c: "#a16207", g: "?", t: "Needs clarification" },
  };
  const p = palette[statusKey] ?? palette.ready;
  return `<span class="neo-plan-verify-lead" style="display:inline-block;margin-right:6px;color:${p.c};font-weight:700;font-size:1.05em;line-height:1;vertical-align:-0.05em;" title="${p.t}" aria-label="${p.t}">${p.g}</span>`;
}

function planVerificationStatusWordHtml(display: string, statusKey: string): string {
  const palette: Record<string, string> = {
    ready: "#15803d",
    partial: "#a16207",
    missing: "#b91c1c",
    "no-op": "#a16207",
    "needs clarification": "#a16207",
  };
  const color = palette[statusKey] ?? "#374151";
  return `<strong style="color:${color};font-weight:600;">${display}</strong>`;
}

/**
 * Markdown source: numbered plan-check lines (`1. Step: Ready — note`).
 * Injects a colored glyph + styles the status word before `marked` parses.
 */
/** Bulleted pre-verification checklist lines: `- **User:** Ready — detail` */
function injectPreVerificationChecklistFormatting(markdown: string): string {
  return markdown.replace(
    /^(-\s+\*\*)([^*]+)(\*\*:\s*)(Ready|Partial|Missing|No-op|Needs clarification)(\s*[—\-–]\s*)/gim,
    (_full, bullet, label, colon, statusRaw, dash) => {
      const statusKey = statusRaw.toLowerCase();
      const display =
        statusRaw.toLowerCase() === "no-op"
          ? "No-op"
          : statusRaw.toLowerCase() === "needs clarification"
            ? "Needs clarification"
            : statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase();
      const icon = planVerificationLeadIconHtml(statusKey);
      const statusSpan = planVerificationStatusWordHtml(display, statusKey);
      return `${bullet}${label}${colon}${icon}${statusSpan}${dash}`;
    }
  );
}

function injectPlanVerificationFormatting(markdown: string): string {
  return markdown.replace(
    /^(\d+\.\s+)(.+?:\s*)(Ready|Partial|Missing|No-op|Needs clarification)(\s*[—\-–]\s*)/gim,
    (_full, num, labelPart, statusRaw, dash) => {
      const statusKey = statusRaw.toLowerCase();
      const display =
        statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase();
      const icon = planVerificationLeadIconHtml(statusKey);
      const statusSpan = planVerificationStatusWordHtml(display, statusKey);
      return `${num}${icon}${labelPart}${statusSpan}${dash}`;
    }
  );
}

function normalizeWorkflowSummaryStatus(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/** e.g. `1. Plan (prerequisites check) — Completed` or `2. Step 1 — Create user — Completed` */
function workflowSummaryTitleCaseStatus(raw: string): string {
  return raw
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");
}

/** Lead icons for post-run workflow summaries (`— Completed`, ` — Skipped`, …). */
function workflowSummaryLeadIconHtml(normalized: string): string {
  const palette: Record<string, { c: string; g: string; t: string }> = {
    completed: { c: "#16a34a", g: "✓", t: "Completed" },
    skipped: { c: "#ca8a04", g: "◐", t: "Skipped" },
    failed: { c: "#dc2626", g: "✗", t: "Failed" },
    pending: { c: "#2563eb", g: "◯", t: "Pending" },
    "in progress": { c: "#2563eb", g: "◷", t: "In progress" },
  };
  const p = palette[normalized] ?? palette.completed;
  return `<span class="neo-workflow-summary-lead" style="display:inline-block;margin-right:6px;color:${p.c};font-weight:700;font-size:1.05em;line-height:1;vertical-align:-0.05em;" title="${p.t}" aria-label="${p.t}">${p.g}</span>`;
}

function workflowSummaryStatusWordHtml(display: string, normalized: string): string {
  const palette: Record<string, string> = {
    completed: "#15803d",
    skipped: "#a16207",
    failed: "#b91c1c",
    pending: "#1d4ed8",
    "in progress": "#1d4ed8",
  };
  const color = palette[normalized] ?? "#374151";
  return `<strong style="color:${color};font-weight:600;">${display}</strong>`;
}

/**
 * Numbered workflow summary lines (`1. … — Completed`) after onboarding completes.
 */
function injectWorkflowSummaryLineFormatting(markdown: string): string {
  return markdown.replace(
    /^(\d+\.\s+)(.+)\s+[—\-–]\s+(In Progress|Completed|Skipped|Failed|Pending)(\s*\([^)]*\))?\s*$/gim,
    (_full, num, body: string, statusRaw: string, detail?: string) => {
      const normalized = normalizeWorkflowSummaryStatus(statusRaw);
      const display = workflowSummaryTitleCaseStatus(statusRaw);
      const icon = workflowSummaryLeadIconHtml(normalized);
      const statusSpan = workflowSummaryStatusWordHtml(display, normalized);
      const trailing = detail ? ` ${detail.trim()}` : "";
      return `${num}${icon}${body} — ${statusSpan}${trailing}`;
    }
  );
}

/** Flatten pre-verification checklist list indentation in chat bubbles. */
function stylePreVerificationChecklistHtml(html: string): string {
  if (!/\bPre-verification\b/i.test(html.replace(/<[^>]+>/g, " "))) {
    return html;
  }
  const headerMatch = html.match(
    /([\s\S]*?<(?:p|strong)[^>]*>\s*<strong[^>]*>Pre-verification<\/strong>[\s\S]*?<\/p>)([\s\S]*)/i
  );
  if (!headerMatch) {
    return html;
  }
  const prefix = headerMatch[1];
  let body = headerMatch[2];
  const actionTitle =
    'class="neo-preverify-action-title" style="margin:0.65em 0 0.2em 0;padding:0;font-weight:600;"';
  const checksList =
    'class="neo-preverify-checks" style="margin:0.15em 0 0.65em 0;padding-left:1.15em;list-style-type:disc;"';
  const checkItem = 'style="margin:0.15em 0;"';

  body = body
    .replace(/<ol[^>]*>/gi, '<div class="neo-preverify-actions">')
    .replace(/<\/ol>/gi, "</div>")
    .replace(/<p><strong>(\d+\.\s+[^<]*)<\/strong><\/p>/gi, `<div ${actionTitle}><strong>$1</strong></div>`)
    .replace(/<ul([^>]*)>/gi, `<ul ${checksList}>`)
    .replace(/<li([^>]*)>/gi, `<li$1 ${checkItem}>`);

  return prefix + body;
}

/**
 * After markdown → HTML: `<li>` from ordered lists often omit the `1.` prefix; still match `Step: Ready —`.
 */
function stylePlanVerificationListItemsHtml(html: string): string {
  return html.replace(/<li(\s[^>]*)?>([\s\S]*?)<\/li>/gi, (full, attrs, inner) => {
    if (inner.includes("neo-plan-verify-lead") || inner.includes("neo-workflow-summary-lead")) return full;
    const plain = inner
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (/.+:\s*(Ready|Partial|Missing|No-op|Needs clarification)\s*[—\-–]/i.test(plain)) {
      const statusMatch = plain.match(/:\s*(Ready|Partial|Missing|No-op|Needs clarification)\s*[—\-–]/i);
      if (!statusMatch?.[1]) return full;
      const statusKey = statusMatch[1].toLowerCase();
      const icon = planVerificationLeadIconHtml(statusKey);
      const innerWithColoredStatus = inner.replace(
        /(Ready|Partial|Missing|No-op|Needs clarification)(\s*[—\-–])/i,
        (_m: string, word: string, dash: string) => {
          const disp =
            word.toLowerCase() === "no-op"
              ? "No-op"
              : word.toLowerCase() === "needs clarification"
                ? "Needs clarification"
                : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          return `${planVerificationStatusWordHtml(disp, word.toLowerCase())}${dash}`;
        }
      );
      return `<li${attrs || ""}>${icon}${innerWithColoredStatus}</li>`;
    }

    const summaryEnd = plain.match(
      /\s+[—\-–]\s+(In Progress|Completed|Skipped|Failed|Pending)(\s*\([^)]*\))?\s*$/i
    );
    if (summaryEnd?.[1]) {
      const normalized = normalizeWorkflowSummaryStatus(summaryEnd[1]);
      const icon = workflowSummaryLeadIconHtml(normalized);
      const innerWithColoredStatus = inner.replace(
        /\s+[—\-–]\s+(In Progress|Completed|Skipped|Failed|Pending)(\s*\([^)]*\))?\s*$/i,
        (_m: string, word: string, detail?: string) => {
          const disp = workflowSummaryTitleCaseStatus(word);
          const n = normalizeWorkflowSummaryStatus(word);
          const trailing = detail ? ` ${detail.trim()}` : "";
          return ` — ${workflowSummaryStatusWordHtml(disp, n)}${trailing}`;
        }
      );
      return `<li${attrs || ""}>${icon}${innerWithColoredStatus}</li>`;
    }

    return full;
  });
}

/**
 * Apply styling transformations to HTML content
 */
const styleHtmlContent = (html: string): string => {
  const completedBadge = '<span style="color: #10B981; font-weight: 600; font-size: 0.95em;">✓ Completed</span>';
  const styleCompletedBadge = (value: string) =>
    value.replace(
      /((?:<br\s*\/?>|\n)+\s*)?✓\s*Completed/gi,
      (_match, lineBreakPrefix?: string) => `${lineBreakPrefix ? " " : ""}${completedBadge}`
    );

  // Style plan step prefixes: purple triangle arrow
  html = html.replace(/▶ Step/g, '<span style="color: #CFBFF8; font-weight: 500;">▶</span> Step');
  
  // Style completion indicator: green checkmark
  html = styleCompletedBadge(html);

  // GFM column alignment (`---:`, `:---`) emits align= on th/td. Chat CSS left-aligns
  // headers but left body cells alone, so right-aligned cells look column-shifted.
  // Strip align attrs so header and body share one horizontal alignment.
  html = html.replace(/<(th|td)([^>]*)>/gi, (_match, tag: string, attrs: string) => {
    const cleaned = String(attrs || "").replace(/\s*align\s*=\s*(["']?)[^"'\s>]+\1/gi, "");
    return `<${tag}${cleaned}>`;
  });
  
  // Style markdown links: add color, underline, and open in new tab (word-break handled by CSS)
  html = html.replace(/<a\s+([^>]*?)>/gi, (_match, attrs) => {
    const withoutTargetRel = attrs
      .replace(/\s*target\s*=\s*["'][^"']*["']/gi, "")
      .replace(/\s*rel\s*=\s*["'][^"']*["']/gi, "")
      .trim();
    const targetRel = ' target="_blank" rel="noopener noreferrer"';
    if (withoutTargetRel.includes("style=")) {
      const withStyle = withoutTargetRel.replace(
        /style="([^"]*)"/i,
        (_match: string, styleValue: string) => `style="${styleValue}; color: #705D48; text-decoration: underline;"`
      );
      return `<a ${withStyle}${targetRel}>`;
    }
    return `<a ${withoutTargetRel} style="color: #705D48; text-decoration: underline;"${targetRel}>`;
  });

  // Render schedule links as the same compact clock affordance used by HITL scheduled actions.
  html = html.replace(
    /<a\s+([^>]*href=(["'])[^"']*\/schedules\?[^"']*scheduleId=[^"']*\2[^>]*)>[\s\S]*?<\/a>/gi,
    (_match, attrs) => {
      const cleanAttrs = String(attrs || "")
        .replace(/\s*target\s*=\s*["'][^"']*["']/gi, "")
        .replace(/\s*rel\s*=\s*["'][^"']*["']/gi, "")
        .replace(/\s*style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/\s*title\s*=\s*["'][^"']*["']/gi, "")
        .replace(/\s*aria-label\s*=\s*["'][^"']*["']/gi, "")
        .trim();
      return `<a ${cleanAttrs} title="Open schedule" aria-label="Open schedule" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;margin-left:6px;border:1px solid #8F6ED5;border-radius:8px;color:#6D55B8;text-decoration:none;vertical-align:middle;background:#FFFFFF;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 6v6l4 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></a>`;
    }
  );

  // Inline markdown images (e.g. remote desktop screenshots): fit the chat column; click opens enlarge (handled in NeoAssistant).
  html = html.replace(
    /<img\s+([^>]*\ssrc=["']data:image\/(?:png|jpeg|gif|webp);base64,[^"']+["'][^>]*)>/gi,
    (_match, attrs) => {
      if (/class\s*=\s*["'][^"']*neo-chat-inline-image/.test(attrs)) {
        return `<img ${attrs}>`;
      }
      return `<img class="neo-chat-inline-image" style="max-width:100%;max-height:320px;width:auto;height:auto;border-radius:8px;cursor:zoom-in;object-fit:contain;display:block;margin:8px 0;" ${attrs}>`;
    }
  );

  html = stylePlanVerificationListItemsHtml(html);
  html = stylePreVerificationChecklistHtml(html);

  // Render export links as an icon-only download link.
  html = html.replace(
    /<a\s+([^>]*href=["'][^"']*\/export_results\/\?params[^"']*["'][^>]*)>[\s\S]*?<\/a>/gi,
    (_match, attrs) =>
      `<a ${attrs} title="Download export" aria-label="Download export"><span style="display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:999px;background:#E8F0FE;color:#2563EB;vertical-align:middle;"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3v10m0 0 4-4m-4 4-4-4M5 15v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span></a>`
  );

  return styleResolverLedgerHtml(html);
};

/**
 * Extensible Markdown div container used by Cortex tool outputs.
 *
 * Cortex wraps pre-rendered Markdown inside a `<div class="neo-markdown" id="...">...</div>`.
 * Marked does not parse Markdown inside raw HTML blocks, so we parse the inner text first
 * and then re-wrap it with the same opening tag attributes.
 */
const parseNeoMarkdownDivs = (markdown: string): string => {
  const re = /<div\s+([^>]*\bneo-markdown\b[^>]*)>([\s\S]*?)<\/div>/gi;
  return markdown.replace(re, (_full, attrs: string, inner: string) => {
    const innerHtml = styleHtmlContent(
      marked.parse(
        injectPreVerificationChecklistFormatting(
          injectPlanVerificationFormatting(injectWorkflowSummaryLineFormatting(inner.trim()))
        )
      ) as string
    );
    return `<div ${attrs}>${innerHtml}</div>`;
  });
};

const normalizeChatContent = (content: string): string => {
  let cleaned = content?.replace(/^content:\s*/, "") ?? "";
  cleaned = cleaned.replace(/\n+$/, "");
  cleaned = renderAttachmentMarkers(cleaned);
  return cleaned.replace(/(✓\s*Completed)\s*/g, "$1\n\n");
};

const attachmentChipHtml = (label: string): string => {
  const safeLabel = escapeHtml(label.trim() || "attachment");
  return `<span class="neo-chat-attachment" title="${safeLabel}" style="display:inline-flex;align-items:center;gap:8px;max-width:100%;box-sizing:border-box;border:1px solid #D1D5DB;background:#F9FAFB;color:#374151;border-radius:10px;padding:6px 10px;margin:4px 0;font-size:12px;line-height:1.35;font-weight:500;vertical-align:middle;"><span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:#EEF2FF;color:#4F46E5;flex:0 0 auto;"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 1 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg></span><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${safeLabel}</span></span>`;
};

const renderAttachmentMarkers = (content: string): string =>
  content.replace(
    /(^|\n)[ \t]*\[(?:(?:Image|Audio|File) attachment|Attachment|Image|Audio|File):\s*([^\]\n]+?)\][ \t]*(?=\n|$)/gi,
    (_match, prefix: string, label: string) => `${prefix}${attachmentChipHtml(label)}`
  );

const renderMarkdownToStyledHtml = (markdown: string): string => {
  const markdownToStyledHtml = (md: string): string =>
    styleHtmlContent(marked.parse(md) as string);

  return markdownToStyledHtml(parseNeoMarkdownDivs(markdown));
};

const extractNoteDisclosure = (
  markdown: string
): { summary: string; details: string } | null => {
  const trimmedLeading = markdown.replace(/^\s+/, "");
  if (!/^NOTE:/i.test(trimmedLeading)) return null;

  const paragraphMatch = trimmedLeading.match(
    /^(NOTE:[\s\S]*?)(?:\n\s*\n+)([\s\S]+)$/i
  );
  if (paragraphMatch) {
    return {
      summary: paragraphMatch[1].trim(),
      details: paragraphMatch[2].trim(),
    };
  }

  const lineMatch = trimmedLeading.match(/^(NOTE:[^\n]*)(?:\n+([\s\S]+))$/i);
  if (lineMatch?.[2]?.trim()) {
    return {
      summary: lineMatch[1].trim(),
      details: lineMatch[2].trim(),
    };
  }

  return null;
};

const splitInvestigationBody = (
  markdown: string
): { summary: string; suffix: string | null; choiceTitle: string; choice: string | null } => {
  const choiceMatch = markdown.match(/^(CHOOSE NEXT STEP|NEXT STEP OPTIONS):\s*/im);
  const suffixMatch = markdown.match(/^\s*(?:-\s*)?(RECOMMENDATION|RECOMMENDATIONS|RECOMMENDED REMEDIATION|RECOMMENDED REMEDIATIONS|CLEANUP SUGGESTION|CLEANUP SUGGESTIONS|NEXT STEP|NEXT STEPS):\s*/im);
  const proseMatch = markdown.match(/^(Let me know if|Please confirm|Please choose|To proceed)[^\n]*/im);
  const candidates = [choiceMatch, suffixMatch, proseMatch].filter(
    (match): match is RegExpMatchArray => Boolean(match && match.index !== undefined)
  );

  if (!candidates.length) {
    return { summary: markdown.trim(), suffix: null, choiceTitle: "", choice: null };
  }

  const firstMatch = candidates.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
  const summary = markdown.slice(0, firstMatch.index).trim();
  const rest = markdown.slice((firstMatch.index ?? 0) + firstMatch[0].length).trim();

  if (firstMatch === choiceMatch) {
    return { summary, suffix: null, choiceTitle: firstMatch[1], choice: rest || null };
  }

  return {
    summary,
    ...splitChoiceFromSuffix(markdown.slice(firstMatch.index).trim()),
  };
};

const splitChoiceFromSuffix = (
  suffixMarkdown: string
): { suffix: string | null; choiceTitle: string; choice: string | null } => {
  const choiceMatch = suffixMarkdown.match(/^(CHOOSE NEXT STEP|NEXT STEP OPTIONS):\s*/im);
  if (!choiceMatch || choiceMatch.index === undefined) {
    return { suffix: suffixMarkdown || null, choiceTitle: "", choice: null };
  }

  return {
    suffix: suffixMarkdown.slice(0, choiceMatch.index).trim() || null,
    choiceTitle: choiceMatch[1],
    choice: suffixMarkdown.slice(choiceMatch.index + choiceMatch[0].length).trim() || null,
  };
};

const extractInvestigationSummary = (
  markdown: string
): { prefix: string; summary: string; suffix: string | null; choiceTitle: string; choice: string | null } | null => {
  const trimmedLeading = markdown.replace(/^\s+/, "");
  const match = trimmedLeading.match(/^INVESTIGATION SUMMARY:\s*/im);
  if (!match || match.index === undefined) return null;

  const prefix = trimmedLeading.slice(0, match.index).trim();
  const body = trimmedLeading.slice(match.index + match[0].length).trim();
  const { summary, suffix, choiceTitle, choice } = splitInvestigationBody(body);
  if (!summary) return null;

  return { prefix, summary, suffix, choiceTitle, choice };
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const noteCalloutHtml = (summary: string, details: string): string => {
  const renderedDetails = details ? `<div style="margin-top:8px;">${renderMarkdownToStyledHtml(details)}</div>` : "";
  return `<div class="neo-note-callout" style="${CHAT_CALLOUT_CONTAINER_STYLE}">${escapeHtml(
    summary
  )}</div>${renderedDetails}`;
};

const choicePromptHtml = (choice: string): string => {
  const optionLines = choice
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const options = optionLines
    .map((line) => line.match(/^(\d+)[.)]\s+(.+)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match));

  if (!options.length) {
    return renderMarkdownToStyledHtml(choice);
  }

  const buttons = options
    .map((match) => {
      const optionNumber = match[1];
      const optionText = match[2].trim();
      const confirmationText = `I choose option ${optionNumber}: ${optionText}`;
      return `<button type="button" class="neo-choice-option-button" data-neo-choice-option="${escapeHtml(
        confirmationText
      )}" style="display:block;width:100%;box-sizing:border-box;text-align:left;border:1px solid #DADCE0;background:#FFFFFF;color:#3c4043;border-radius:8px;padding:10px 12px;margin:8px 0;cursor:pointer;font:inherit;line-height:1.45;"><strong style="margin-right:6px;">${escapeHtml(
        optionNumber
      )}.</strong>${escapeHtml(optionText)}</button>`;
    })
    .join("");

  return `<div>${buttons}</div>`;
};

const investigationSummaryHtml = (
  prefix: string,
  summary: string,
  suffix: string | null,
  choiceTitle: string,
  choice: string | null
): string => {
  const renderedPrefix = prefix ? `<div>${renderMarkdownToStyledHtml(prefix)}</div>` : "";
  const renderedSummary = renderMarkdownToStyledHtml(summary);
  const renderedSuffix = suffix ? `<div>${renderMarkdownToStyledHtml(suffix)}</div>` : "";
  const renderedChoice = choice
    ? `<div class="neo-choose-next-step-card" style="${CHOOSE_NEXT_STEP_CARD_STYLE}"><div style="${INVESTIGATION_SUMMARY_TITLE_STYLE}">${
        choiceTitle.toUpperCase() === "NEXT STEP OPTIONS" ? "Next step options" : "Choose a next step"
      }</div><div style="${INVESTIGATION_SUMMARY_BODY_STYLE}">${choicePromptHtml(choice)}</div></div>`
    : "";

  return `${renderedPrefix}<div class="neo-investigation-summary-card" style="${INVESTIGATION_SUMMARY_CARD_STYLE}"><div style="${INVESTIGATION_SUMMARY_TITLE_STYLE}">Investigation Summary</div><div style="${INVESTIGATION_SUMMARY_BODY_STYLE}">${renderedSummary}</div></div>${renderedSuffix}${renderedChoice}`;
};

/**
 * Renders stored chat markdown to styled HTML (links, plans, exports).
 * Any `.neo-markdown` div: inner Markdown is expanded (see above).
 */
export const cleanContent = (content: string): string => {
  const normalized = normalizeChatContent(content);
  const cleaned = injectPreVerificationChecklistFormatting(
    injectPlanVerificationFormatting(injectWorkflowSummaryLineFormatting(normalized))
  );

  // Configure marked to preserve line breaks and handle paragraphs
  marked.setOptions({
    breaks: true, // Convert single \n to <br>
    gfm: true, // GitHub Flavored Markdown
  });

  const investigationSummary = extractInvestigationSummary(cleaned);
  if (investigationSummary) {
    return investigationSummaryHtml(
      investigationSummary.prefix,
      investigationSummary.summary,
      investigationSummary.suffix,
      investigationSummary.choiceTitle,
      investigationSummary.choice
    );
  }

  const noteDisclosure = extractNoteDisclosure(cleaned);
  if (noteDisclosure) {
    return noteCalloutHtml(noteDisclosure.summary, noteDisclosure.details);
  }

  return renderMarkdownToStyledHtml(cleaned);
};

export const getSender = (msg: any, currentUser: any) =>
  msg.user_id === "neo"
    ? { sender: "assistant", senderName: "Theo Assistant" }
    : {
        sender: "user",
        senderName: currentUser?.name || "You",
        avatar: currentUser?.avatar || currentUser?.name?.[0],
      };
